import type {
  ReadSearchApiArg,
  SearchFilterCondition,
  SearchFilters,
  SearchSort,
  SearchSortField,
} from 'lib-search-types';
import { convertChannelCasing } from 'lib-shared-api-utils';
import type { ConversationStatus, Sort } from 'lib-shared-types-api';

import { toBoolean } from 'utils/formatting/string';
import { getSortObjectFromString } from 'utils/url/getSortObjectFromString';
import { AppSearchParams } from 'utils/url/useAppSearchParams';

/** The default conversations page size. */
export const defaultConversationsPageSize = 50;

/** Valid sort fields for conversations scope in the search API. */
const validSortFields = [
  'created_at',
  'last_message_created_at',
] satisfies SearchSortField<'conversations'>[];

export function transformConversationsParamsToSearchApiArg(
  params: AppSearchParams<'/conversations'>,
): ReadSearchApiArg<'conversations', true> {
  const conditions: SearchFilterCondition<'conversations'>[] = [];

  // Agent / assignment filter (mutually exclusive in URL)
  if (params.assigned) {
    const isAssigned = toBoolean(params.assigned);

    conditions.push({
      field: 'agent_id',
      operator: isAssigned ? 'is_not_empty' : 'is_empty',
      values: [],
    });
  } else if (params.agent) {
    conditions.push({
      field: 'agent_id',
      operator: 'is_one',
      values: toArray(params.agent).map((value) => ({ value })),
    });
  }

  // Channel filter
  if (params.channel) {
    conditions.push({
      field: 'initial_channel',
      operator: 'is_one',
      values: toArray(params.channel).map((channel) => ({
        value: convertChannelCasing(channel, 'truncate'),
      })),
    });
  }

  // Direction filter
  if (params.direction) {
    conditions.push({
      field: 'initial_direction',
      operator: 'is_one',
      values: toArray(params.direction).map((value) => ({ value })),
    });
  }

  // Queue / queued filter (mutually exclusive in URL)
  if (params.queued) {
    const isQueued = toBoolean(params.queued);

    conditions.push({
      field: 'queue_id',
      operator: isQueued ? 'is_not_empty' : 'is_empty',
      values: [],
    });
  } else if (params.queue) {
    conditions.push({
      field: 'queue_id',
      operator: 'is_one',
      values: toArray(params.queue).map((value) => ({ value })),
    });
  }

  // Requester filter
  if (params.requester) {
    conditions.push({
      field: 'requested_by',
      operator: 'is_one',
      values: toArray(params.requester).map((value) => ({ value })),
    });
  }

  // Status filter (enriched: open/pending implies awaitingpending, closed implies pending)
  if (params.status) {
    const enriched = new Set<ConversationStatus>(
      toArray(params.status).filter(Boolean) as ConversationStatus[],
    );

    if (enriched.has('open') || enriched.has('pending')) {
      enriched.add('awaitingpending');
    }
    if (enriched.has('closed')) {
      enriched.add('pending');
    }

    conditions.push({
      field: 'status',
      operator: 'is_one',
      values: Array.from(enriched).map((value) => ({ value })),
    });
  }

  const filters: SearchFilters<'conversations'> | undefined =
    conditions.length > 0
      ? { strategy: 'all', filters: conditions }
      : undefined;

  const {
    key: field,
    direction: order,
  }: Sort<SearchSortField<'conversations'>> = (params.sort &&
    getSortObjectFromString(params.sort, validSortFields)) || {
    key: 'created_at',
    direction: 'desc',
  };
  const sortBy: SearchSort<'conversations'> = {
    field,
    order: order ?? 'desc',
  };

  const pageSize = params.limit
    ? parseInt(params.limit)
    : defaultConversationsPageSize;
  const page = params.offset
    ? Math.floor(parseInt(params.offset) / pageSize)
    : undefined;

  return {
    scope: 'conversations',
    ...(filters ? { filters } : {}),
    sortBy,
    ...(page ? { page } : {}),
    pageSize,
    enrichmentSpec: 'ALL',
  };
}

// Normalize a param that may be a single value or an array to always be an array.
const toArray = <T>(value: T | readonly T[]): readonly T[] =>
  Array.isArray(value) ? (value as readonly T[]) : [value as T];
